import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import CourseLibrary from "@/pages/CourseLibrary";
import Workspace from "@/pages/Workspace";
import Account from "@/pages/Account";
import Billing from "@/pages/Billing";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import AdminPortal from "@/pages/AdminPortal";
import { TutorPortal, TutorAssignment } from "@/pages/TutorPortal";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#0B5D3B] border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => {
    api.post("/analytics/pageview", { path: location.pathname }).catch(() => {});
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AnalyticsTracker />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<PublicOnly><Auth /></PublicOnly>} />
            <Route path="/app" element={<Protected><AppLayout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="courses" element={<CourseLibrary />} />
              <Route path="billing" element={<Billing />} />
              <Route path="assignments/:id" element={<Workspace />} />
              <Route path="account" element={<Account />} />
              <Route path="admin" element={<AdminPortal />} />
              <Route path="tutor" element={<TutorPortal />} />
              <Route path="tutor/:id" element={<TutorAssignment />} />
              <Route path="payment/success" element={<PaymentSuccess />} />
              <Route path="payment/cancel" element={<PaymentCancel />} />
            </Route>
            <Route path="/payment/success" element={<Protected><AppLayout /></Protected>}>
              <Route index element={<PaymentSuccess />} />
            </Route>
            <Route path="/payment/cancel" element={<Protected><AppLayout /></Protected>}>
              <Route index element={<PaymentCancel />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
